import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useLocation, Link } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import {
  FaAddressCard,
  FaTimes,
  FaEye,
  FaSearch,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaThumbsUp,
  FaThumbsDown,
  FaCog,
  FaSync,
  FaSmile,
  FaFrown,
} from 'react-icons/fa';

import { useAuth } from '~/hooks';
import api from '~/services/api';
import history from '~/services/history';

import formatDocument from '~/util/formatDocument';
import formatContact from '~/util/formatContact';
import findAndFormatContact from '~/util/findAndFormatContact';

import {
  DatePicker,
  Input,
  InputMask,
  Select,
  SelectWithAddNewOptionButton,
} from '~/components/Form';
import { TableLoading, TableContainer } from '~/components/Table';
import Loading from '~/components/Loading';
import ConfirmWindow from '~/components/ConfirmWindow';
import PermissionComponent from '~/components/PermissionComponent';
import PopUp from '~/components/Popup';
import Pagination from '~/components/Pagination';

import {
  Container,
  Header,
  Controls,
  Filter,
  Content,
  DetailsContainer,
  BasicInfo,
  Addresses,
  Address,
  Contacts,
  RelatedList,
  Related,
  FormContainer,
  Button,
} from '~/pages/Client/List/styles';
import { FaCloudArrowUp } from 'react-icons/fa6';

const List = () => {
  const { company, companyUser, user } = useAuth();

  const { state } = useLocation();

  let matchParamsId;

  if (state) {
    matchParamsId = state.id;
  } else {
    matchParamsId = null;
  }

  const filterRef = useRef(null);
  const formRef = useRef(null);

  const [visiblePopUp, setVisiblePopUp] = useState(false);

  const [showTable, setShowTable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [client, setClient] = useState({});
  const [clientIndex, setClientIndex] = useState(0);
  const [totalClients, setTotalClients] = useState(0);

  const [currentPage, setCurrentPage] = useState(() => {
    const currentPageStorage = sessionStorage.getItem(
      '@Diretiva:controle:page'
    );

    if (currentPageStorage) {
      return Number(currentPageStorage);
    }

    return 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // seleção de controles para ações em massa (ex: transmitir boletos)
  const [selectedIds, setSelectedIds] = useState([]);
  const [billetsCount, setBilletsCount] = useState(0);

  const handleIdsChange = id => {
    if (id === 'all') {
      // selecionar todos ou limpar seleção
      if (selectedIds.length === clients.length) {
        setSelectedIds([]);
      } else {
        setSelectedIds(clients.map(c => c.id));
      }
      return;
    }

    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(i => i !== id);
      }
      return [...prev, id];
    });
  };

  const [endDate, setEndDate] = useState('');
  const [inactiveType, setInactiveType] = useState(null);
  const [inactiveTypeOptions, setInactiveTypeOptions] = useState([]);

  const [nameSearch, setNameSearch] = useState(() => {
    const nameStorage = localStorage.getItem('@Diretiva:controle:filter:name');

    if (nameStorage) {
      return nameStorage;
    }

    return '';
  });
  const [competenceFilter, setCompetenceFilter] = useState(() => {
    const compStorage = localStorage.getItem(
      '@Diretiva:controle:filter:competence'
    );

    if (compStorage) {
      // compStorage may be stored as 'YYYY-MM' (old) or 'MM/YYYY' (new) => normalize to YYYYMM for API
      if (compStorage.indexOf('-') !== -1) {
        return compStorage.replace('-', '');
      }
      if (compStorage.indexOf('/') !== -1) {
        const parts = compStorage.split('/'); // MM / YYYY
        if (parts.length === 2) {
          const mm = parts[0].padStart(2, '0');
          const yyyy = parts[1];
          return `${yyyy}${mm}`;
        }
      }

      // fallback: remove non digits and try to return YYYYMM
      const digits = compStorage.replace(/[^0-9]/g, '');
      if (digits.length === 6) {
        if (digits.substring(0, 4).match(/^\d{4}$/)) return digits; // assume YYYYMM
        return `${digits.substring(2)}${digits.substring(0, 2)}`; // assume MMYYYY -> YYYYMM
      }
    }

    // If no stored value, set to current competence
    const currentDate = new Date();
    const currentMonth = (currentDate.getMonth() + 1)
      .toString()
      .padStart(2, '0');
    const currentYear = currentDate.getFullYear();
    return `${currentYear}${currentMonth}`;
  });
  const [situationOptions, setSituationOptions] = useState([]);
  const [selectedSituation, setSelectedSituation] = useState(() => {
    const situationStorage = localStorage.getItem(
      '@Diretiva:controle:filter:situation'
    );

    if (situationStorage) {
      try {
        const parsed = JSON.parse(situationStorage);
        if (parsed && parsed.value !== undefined) {
          return parsed;
        }
      } catch (error) {
        // ignore malformed persisted value
      }
    }

    return {
      value: 'Aguardando envio para o banco',
      label: 'Aguardando envio para o banco',
    };
  });
  const monthlyOptions = useMemo(
    () => [
      { value: '', label: 'Todos' },
      { value: 'with', label: 'Com mensalidade' },
      { value: 'without', label: 'Sem mensalidade' },
    ],
    []
  );
  const [selectedMonthly, setSelectedMonthly] = useState(() => {
    const monthlyStorage = localStorage.getItem(
      '@Diretiva:controle:filter:monthly'
    );

    if (monthlyStorage) {
      try {
        const parsed = JSON.parse(monthlyStorage);
        if (parsed && parsed.value !== undefined) {
          return parsed;
        }
      } catch (error) {
        // ignore malformed data and fallback to default option
      }
    }

    return { value: '', label: 'Todos' };
  });
  const consistencyOptions = useMemo(
    () => [
      { value: '', label: 'Todos' },
      { value: 'consistent', label: 'Consistente' },
      { value: 'inconsistent', label: 'Inconsistente' },
    ],
    []
  );
  const [selectedConsistency, setSelectedConsistency] = useState(() => {
    const consistencyStorage = localStorage.getItem(
      '@Diretiva:controle:filter:consistency'
    );

    if (consistencyStorage) {
      try {
        const parsed = JSON.parse(consistencyStorage);
        if (parsed && parsed.value !== undefined) {
          return parsed;
        }
      } catch (error) {
        // ignore malformed value and fallback to default
      }
    }

    return { value: '', label: 'Todos' };
  });

  // helper to format stored YYYYMM to MM/YYYY for masked input
  const defaultCompetence = competenceFilter
    ? `${competenceFilter.substring(4)}/${competenceFilter.substring(0, 4)}`
    : '';

  const loadSituationOptions = useCallback(async () => {
    try {
      const response = await api.get('/billet/billet-situations');

      let options = [];
      if (Array.isArray(response.data) && response.data.length > 0) {
        options = response.data
          .map(item => ({
            value: item.description,
            label: item.description,
          }))
          .filter(
            option => option.value !== undefined && option.value !== null
          );
      } else {
        toast.warn('Nenhuma situação de boleto foi encontrada.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }

      options.unshift(
        { value: '', label: 'Todos' },
        { value: '-9999', label: 'Excluídos' }
      );

      setSituationOptions(options);
    } catch (err) {
      toast.error('Falha ao buscar situações de boleto.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, []);

  const loadClients = useCallback(async () => {
    if (company) {
      try {
        if (matchParamsId) {
          setShowTable(false);
          setLoading(true);

          const response = await api.get(`relationships/${matchParamsId}`);

          const { data } = response;

          const formattedClient = {
            ...data,
            monthly_payments_total:
              data.monthly_payments && data.monthly_payments.length > 0
                ? data.monthly_payments.reduce(
                    (sum, mp) => sum + (mp.price || 0),
                    0
                  )
                : 0,
            billets_total:
              data.billets && data.billets.length > 0
                ? data.billets.reduce((sum, b) => sum + (b.price || 0), 0)
                : 0,
            birthday: data.birthday
              ? format(parseISO(data.birthday), 'dd/MM/yyyy')
              : '',
            start_day: data.start_day
              ? format(parseISO(data.start_day), 'dd/MM/yyyy')
              : '',
            end_day: data.end_day
              ? format(parseISO(data.end_day), 'dd/MM/yyyy')
              : '',
            document: data.document
              ? formatDocument(data.document_type, data.document)
              : '',
            cei: data.cei ? formatDocument(null, data.cei) : '',
            phone: data.phone
              ? formatContact(2, data.phone.replace(/[^0-9]+/g, ''))
              : '',
            addresses:
              data.addresses && data.addresses.length > 0
                ? data.addresses.map(address => ({
                    ...address,
                    complement:
                      address.complement === 'Array' ? '' : address.complement,
                  }))
                : [],
            contacts:
              data.contacts && data.contacts.length > 0
                ? data.contacts.map(contact => ({
                    ...contact,
                    content: contact.content
                      ? formatContact(
                          contact.type,
                          contact.type !== 1
                            ? contact.content.replace(/[^0-9]+/g, '')
                            : contact.content
                        )
                      : '',
                  }))
                : [],
            related:
              data.related && data.related.length > 0
                ? data.related.map(related => ({
                    id: related.id,
                    dpt_labour: related.dpt_labour,
                    dpt_tax: related.dpt_tax,
                    dpt_accounting: related.dpt_accounting,
                    dpt_financial: related.dpt_financial,
                    dpt_admin: related.dpt_admin,
                    responsible: related.responsible,
                    name: (related.info && related.info.name) || '',
                    document:
                      related.info && related.info.document
                        ? formatDocument(
                            related.info.document_type,
                            related.info.document
                          )
                        : '',
                    birthday:
                      related.info && related.info.birthday
                        ? format(parseISO(related.info.birthday), 'dd/MM/yyyy')
                        : '',
                    email:
                      related.related_contacts &&
                      related.related_contacts.length > 0
                        ? findAndFormatContact(
                            related.related_contacts,
                            'email'
                          )
                        : '',
                    contact:
                      related.related_contacts &&
                      related.related_contacts.length > 0
                        ? findAndFormatContact(
                            related.related_contacts,
                            'contact'
                          )
                        : '',
                  }))
                : [],
          };

          setClient(formattedClient);
        } else {
          setShowTable(true);
          // build params and include competence if set (YYYYMM)
          const params = {
            company_id: company.id,
            page: currentPage,
            type: 1,
            search: nameSearch,
            active: 1, // manter Situação = Ativo
            status: true, // manter status = true
            client_type: 0, // filtrar apenas client_type = 0
            includeMonthlyPayments: true,
            include_billets: true,
            situation_text: selectedSituation?.value || '',
          };

          if (competenceFilter) {
            // API expects competence as MM/YYYY in this endpoint
            if (competenceFilter.length === 6) {
              params.competence = `${competenceFilter.substring(
                4
              )}/${competenceFilter.substring(0, 4)}`;
            } else {
              params.competence = competenceFilter;
            }
          }

          if (selectedMonthly?.value === 'with') {
            params.has_monthly_payments = true;
          } else if (selectedMonthly?.value === 'without') {
            params.has_monthly_payments = false;
          }

          if (selectedConsistency?.value) {
            params.consistency = selectedConsistency.value;
          }

          const response = await api.get(
            'relationships/with-monthly-payments',
            { params }
          );

          if (response.data.docs.length > 0) {
            const formattedClients = response.data.docs.map(item => ({
              ...item,
              birthday: item.birthday
                ? format(parseISO(item.birthday), 'dd/MM/yyyy')
                : '',
              monthly_payments_total:
                item.monthly_payments && item.monthly_payments.length > 0
                  ? item.monthly_payments.reduce(
                      (sum, mp) => sum + (mp.price || 0),
                      0
                    )
                  : 0,
              billets_total:
                item.billets && item.billets.length > 0
                  ? item.billets.reduce((sum, b) => sum + (b.price || 0), 0)
                  : 0,
              start_day: item.start_day
                ? format(parseISO(item.start_day), 'dd/MM/yyyy')
                : '',
              end_day: item.end_day
                ? format(parseISO(item.end_day), 'dd/MM/yyyy')
                : '',
              contract_date: item.contract_date
                ? format(parseISO(item.contract_date), 'dd/MM/yyyy')
                : '',
              document: item.document
                ? formatDocument(item.document_type, item.document)
                : '',
              cei: item.cei ? formatDocument(null, item.cei) : '',
              phone: item.phone
                ? formatContact(2, item.phone.replace(/[^0-9]+/g, ''))
                : '',
              addresses:
                item.addresses && item.addresses.length > 0
                  ? item.addresses.map(address => ({
                      ...address,
                      complement:
                        address.complement === 'Array'
                          ? ''
                          : address.complement,
                    }))
                  : [],
              contacts:
                item.contacts && item.contacts.length > 0
                  ? item.contacts.map(contact => ({
                      ...contact,
                      content: contact.content
                        ? formatContact(
                            contact.type,
                            contact.type !== 1
                              ? contact.content.replace(/[^0-9]+/g, '')
                              : contact.content
                          )
                        : '',
                    }))
                  : [],
              related:
                item.related && item.related.length > 0
                  ? item.related.map(related => ({
                      id: related.id,
                      dpt_labour: related.dpt_labour,
                      dpt_tax: related.dpt_tax,
                      dpt_accounting: related.dpt_accounting,
                      dpt_financial: related.dpt_financial,
                      dpt_admin: related.dpt_admin,
                      responsible: related.responsible,
                      name: (related.info && related.info.name) || '',
                      treatment_name:
                        (related.info && related.info.treatment_name) || '',
                      document:
                        related.info && related.info.document
                          ? formatDocument(
                              related.info.document_type,
                              related.info.document
                            )
                          : '',
                      birthday:
                        related.info && related.info.birthday
                          ? format(
                              parseISO(related.info.birthday),
                              'dd/MM/yyyy'
                            )
                          : '',
                      email:
                        related.related_contacts &&
                        related.related_contacts.length > 0
                          ? findAndFormatContact(
                              related.related_contacts,
                              'email'
                            )
                          : '',
                      contact:
                        related.related_contacts &&
                        related.related_contacts.length > 0
                          ? findAndFormatContact(
                              related.related_contacts,
                              'contact'
                            )
                          : '',
                    }))
                  : [],
              audit:
                item.audit && item.audit.length > 0
                  ? item.audit.map(audit => ({
                      ...audit,
                      formatted_audit_date: format(
                        parseISO(audit.created_at),
                        "dd/MM/yyyy 'às' HH:mm"
                      ),
                    }))
                  : [],
            }));

            const filteredClients = formattedClients.filter(clientItem => {
              if (selectedMonthly?.value === 'with') {
                return (
                  Array.isArray(clientItem.monthly_payments) &&
                  clientItem.monthly_payments.length > 0
                );
              }
              if (selectedMonthly?.value === 'without') {
                return (
                  !Array.isArray(clientItem.monthly_payments) ||
                  clientItem.monthly_payments.length === 0
                );
              }
              return true;
            });

            setClients(filteredClients);
            if (!matchParamsId && filteredClients.length > 0) {
              setClient(filteredClients[0]);
            } else if (filteredClients.length === 0) {
              setClient({});
            }
            setTotalPages(response.data.pages);
            setTotalClients(response.data.total);
            // store page size returned by API (fallback to docs length)
            setPageSize(response.data.limit || response.data.docs.length || 10);
          } else {
            setClients([]);
            setClient({});
            setTotalPages(1);
            setTotalClients(0);
            toast.warn('Nenhum registro foi encontrado.', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        }

        setLoading(false);
      } catch (err) {
        toast.error('Falha ao buscar registros.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setLoading(false);
      }
    }
  }, [
    company,
    currentPage,
    nameSearch,
    matchParamsId,
    competenceFilter,
    selectedSituation?.value,
    selectedMonthly?.value,
    selectedConsistency?.value,
  ]);

  useEffect(() => {
    loadSituationOptions();
  }, [loadSituationOptions]);

  useEffect(() => {
    if (!selectedSituation || situationOptions.length === 0) {
      return;
    }

    const hasOption = situationOptions.some(
      option => option.value === selectedSituation.value
    );

    if (!hasOption) {
      const fallbackOption =
        situationOptions.find(option => option.value === '') ||
        situationOptions[0];

      if (fallbackOption) {
        setSelectedSituation(fallbackOption);
        localStorage.setItem(
          '@Diretiva:controle:filter:situation',
          JSON.stringify(fallbackOption)
        );
        if (
          filterRef.current &&
          typeof filterRef.current.setFieldValue === 'function'
        ) {
          filterRef.current.setFieldValue('situation', fallbackOption);
        }
      }
      return;
    }

    if (
      filterRef.current &&
      typeof filterRef.current.getFieldValue === 'function' &&
      typeof filterRef.current.setFieldValue === 'function'
    ) {
      const currentValue = filterRef.current.getFieldValue('situation');
      if (currentValue !== selectedSituation.value) {
        filterRef.current.setFieldValue('situation', selectedSituation);
      }
    }
  }, [selectedSituation, situationOptions]);

  useEffect(() => {
    if (!Array.isArray(monthlyOptions) || monthlyOptions.length === 0) {
      return;
    }

    const matchedOption = monthlyOptions.find(
      option => option.value === selectedMonthly?.value
    );

    if (!matchedOption) {
      const fallbackOption = monthlyOptions[0];
      setSelectedMonthly(fallbackOption);
      localStorage.setItem(
        '@Diretiva:controle:filter:monthly',
        JSON.stringify(fallbackOption)
      );
      return;
    }

    if (matchedOption.label !== selectedMonthly.label) {
      setSelectedMonthly(matchedOption);
      localStorage.setItem(
        '@Diretiva:controle:filter:monthly',
        JSON.stringify(matchedOption)
      );
    }
  }, [monthlyOptions, selectedMonthly]);

  useEffect(() => {
    if (
      !filterRef.current ||
      typeof filterRef.current.setFieldValue !== 'function'
    ) {
      return;
    }

    const currentValue = filterRef.current.getFieldValue
      ? filterRef.current.getFieldValue('monthly')
      : null;

    if (currentValue !== selectedMonthly?.value) {
      filterRef.current.setFieldValue('monthly', selectedMonthly);
    }
  }, [selectedMonthly]);

  useEffect(() => {
    if (!Array.isArray(consistencyOptions) || consistencyOptions.length === 0) {
      return;
    }

    const matchedOption = consistencyOptions.find(
      option => option.value === selectedConsistency?.value
    );

    if (!matchedOption) {
      const fallbackOption = consistencyOptions[0];
      setSelectedConsistency(fallbackOption);
      localStorage.setItem(
        '@Diretiva:controle:filter:consistency',
        JSON.stringify(fallbackOption)
      );
      return;
    }

    if (matchedOption.label !== selectedConsistency.label) {
      setSelectedConsistency(matchedOption);
      localStorage.setItem(
        '@Diretiva:controle:filter:consistency',
        JSON.stringify(matchedOption)
      );
    }
  }, [consistencyOptions, selectedConsistency]);

  useEffect(() => {
    if (
      !filterRef.current ||
      typeof filterRef.current.setFieldValue !== 'function'
    ) {
      return;
    }

    const currentValue = filterRef.current.getFieldValue
      ? filterRef.current.getFieldValue('consistency')
      : null;

    if (currentValue !== selectedConsistency?.value) {
      filterRef.current.setFieldValue('consistency', selectedConsistency);
    }
  }, [selectedConsistency]);

  useEffect(() => {
    async function loadInactiveTypeOptions() {
      if (company) {
        try {
          const response = await api.get(
            `relationships/inactive-types/${company.id}`
          );
          setInactiveTypeOptions(response.data);
        } catch (err) {
          toast.error('Falha ao carregar tipos de encerramento.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      }
    }

    loadInactiveTypeOptions();
    loadClients();
  }, [loadClients, company]);

  const alterView = useCallback(() => {
    if (matchParamsId) {
      history.push('/controle', { id: null });
    }
    if (clients.length > 0) {
      setShowTable(old => !old);
    }
  }, [clients.length, matchParamsId]);

  const handleDelete = useCallback(
    async id => {
      try {
        setDeleteLoading(true);

        await api.delete(`relationships/${id}`);

        setClients(oldClients => oldClients.filter(item => item.id !== id));

        alterView();

        loadClients();

        toast.success('Registro deletado com sucesso.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setDeleteLoading(false);
      } catch (err) {
        toast.error('Falha ao deletar registro.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setDeleteLoading(false);
      }
    },
    [alterView, loadClients]
  );

  const confirmDelete = useCallback(() => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={() => handleDelete(client && client.id)}
            onClose={onClose}
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleDelete, client]);

  const handleTransmitPayments = useCallback(async () => {
    try {
      setLoading(true);

      const selectedClients = clients.filter(c => selectedIds.includes(c.id));
      const billetIds = selectedClients.reduce((acc, cli) => {
        if (cli.billets && cli.billets.length > 0) {
          const ids = cli.billets.map(b => b.id).filter(Boolean);
          return acc.concat(ids);
        }
        return acc;
      }, []);

      if (billetIds.length === 0) {
        toast.warn('Nenhum boleto encontrado nos controles selecionados.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setLoading(false);
        return;
      }

      const response = await api.post(
        '/billet/transmit-payment',
        { data: billetIds },
        {
          params: {
            level: companyUser?.level,
            user_id: user?.id,
          },
        }
      );

      toast.success('Boleto(s) transmitido(s) com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });

      if (response.data && response.data.transmitted > 0) {
        loadClients();
      }
    } catch (error) {
      const errMsg =
        JSON.stringify(error.response?.data?.error) ||
        error.message ||
        'Erro desconhecido';
      toast.error(`Nao foi possivel transmitir o(s) boleto(s): ${errMsg}`, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setSelectedIds([]);
      setLoading(false);
    }
  }, [clients, companyUser, loadClients, selectedIds, user]);

  const confirmTransmitPayments = useCallback(() => {
    if (selectedIds.length === 0) {
      toast.warn('Nenhum controle foi selecionado para transmissão.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <ConfirmWindow
            onClick={handleTransmitPayments}
            onClose={onClose}
            message="Todos os boletos dos controles selecionados serão transmitidos."
          />
        );
      },
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }, [handleTransmitPayments, selectedIds]);

  const loadBilletsCount = useCallback(async () => {
    try {
      if (!competenceFilter) {
        setBilletsCount(0);
        return;
      }

      const response = await api.get(
        `/billet/count-payments-by-competence/${competenceFilter}`
      );

      setBilletsCount(response.data.count || 0);
    } catch (error) {
      // Silenciar erro ao buscar contagem
      setBilletsCount(0);
    }
  }, [competenceFilter]);

  useEffect(() => {
    loadBilletsCount();
  }, [loadBilletsCount]);

  const handleCheckPaymentsByCompetence = useCallback(async () => {
    try {
      if (!competenceFilter) {
        toast.warn('Selecione uma competência para verificar os pagamentos.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }

      setLoading(true);

      await api.get(`/billet/check-payments-by-competence/${competenceFilter}`);

      toast.success('Pagamentos Atualizados com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });

      // Recarrega a lista após verificar os pagamentos
      loadClients();
      loadBilletsCount();
    } catch (error) {
      const errMsg =
        error.response?.data?.message ||
        error.message ||
        'Erro ao verificar pagamentos';
      toast.error(`Falha ao verificar pagamentos: ${errMsg}`, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  }, [competenceFilter, loadClients, loadBilletsCount]);

  const handleInativarPJ = useCallback(async () => {
    try {
      if (!endDate || !inactiveType) {
        toast.error('Preencha todos os campos antes de salvar.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      }

      await api.post(`relationships/inactivate/${client.id}`, {
        end_day: endDate,
        inactive_type: inactiveType.value,
      });

      toast.success('Registro inativado com sucesso.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });

      setVisiblePopUp(false);
      loadClients();
    } catch (err) {
      toast.error('Falha ao inativar registro.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [client.id, endDate, inactiveType, loadClients]);

  const getDetails = useCallback(
    (id, index) => {
      const item = clients.find(clientItem => clientItem.id === id);

      setClient(item || {});
      setClientIndex(typeof index === 'number' ? index : 0);
      alterView();
    },
    [clients, alterView]
  );

  const handlePage = useCallback(
    page => {
      if (page === 0) {
        setCurrentPage(1);
        sessionStorage.setItem('@Diretiva:controle:page', 1);
      } else if (page > totalPages) {
        setCurrentPage(totalPages);
        sessionStorage.setItem('@Diretiva:controle:page', totalPages);
      } else {
        setCurrentPage(page);
        sessionStorage.setItem('@Diretiva:controle:page', page);
      }
    },
    [totalPages]
  );

  const handlePrevItem = useCallback(() => {
    if (!clients || clients.length === 0) return;
    if (clientIndex !== 0) {
      setClientIndex(clientIndex - 1);
      setClient(clients[clientIndex - 1] || {});
    } else {
      setClientIndex(clients.length - 1);
      setClient(clients[clients.length - 1] || {});
    }
  }, [clientIndex, clients]);

  const handleNextItem = useCallback(() => {
    if (!clients || clients.length === 0) return;
    if (clientIndex !== clients.length - 1) {
      setClientIndex(clientIndex + 1);
      setClient(clients[clientIndex + 1] || {});
    } else {
      setClientIndex(0);
      setClient(clients[0] || {});
    }
  }, [clientIndex, clients]);

  const absoluteIndex = (() => {
    const size = pageSize || clients.length || 1;
    return (currentPage - 1) * size + (clientIndex + 1);
  })();

  const searchByName = useCallback(() => {
    const name = filterRef.current.getFieldValue('name');
    const competenceInput = filterRef.current.getFieldValue('competence');
    const situationValue = filterRef.current.getFieldValue('situation');
    const monthlyValue = filterRef.current.getFieldValue('monthly');
    const consistencyValue = filterRef.current.getFieldValue('consistency');

    // competenceInput expected in format MM/YYYY from InputMask
    let competenceValue = '';
    if (competenceInput) {
      const digits = competenceInput.replace(/[^0-9]/g, ''); // MMYYYY
      if (digits.length === 6) {
        const mm = digits.substring(0, 2);
        const yyyy = digits.substring(2);
        competenceValue = `${yyyy}${mm}`; // YYYYMM for API
      }
    }

    let selectedOption = null;

    if (Array.isArray(situationOptions) && situationOptions.length > 0) {
      selectedOption = situationOptions.find(
        option => option.value === situationValue
      );

      if (!selectedOption) {
        selectedOption = situationOptions.find(option => option.value === '');
      }
    }

    if (!selectedOption) {
      selectedOption = {
        value: situationValue || '',
        label:
          situationValue && situationValue !== '' ? situationValue : 'Todos',
      };
    }

    let selectedMonthlyOption = null;

    if (Array.isArray(monthlyOptions) && monthlyOptions.length > 0) {
      selectedMonthlyOption = monthlyOptions.find(
        option => option.value === monthlyValue
      );

      if (!selectedMonthlyOption) {
        selectedMonthlyOption = monthlyOptions.find(
          option => option.value === ''
        );
      }
    }

    if (!selectedMonthlyOption) {
      selectedMonthlyOption = {
        value: monthlyValue || '',
        label: monthlyValue && monthlyValue !== '' ? monthlyValue : 'Todos',
      };
    }

    let selectedConsistencyOption = null;

    if (Array.isArray(consistencyOptions) && consistencyOptions.length > 0) {
      selectedConsistencyOption = consistencyOptions.find(
        option => option.value === consistencyValue
      );

      if (!selectedConsistencyOption) {
        selectedConsistencyOption = consistencyOptions.find(
          option => option.value === ''
        );
      }
    }

    if (!selectedConsistencyOption) {
      selectedConsistencyOption = {
        value: consistencyValue || '',
        label:
          consistencyValue && consistencyValue !== ''
            ? consistencyValue
            : 'Todos',
      };
    }

    setCurrentPage(1);
    setNameSearch(name);
    setCompetenceFilter(competenceValue);
    setSelectedSituation(selectedOption);
    setSelectedMonthly(selectedMonthlyOption);
    setSelectedConsistency(selectedConsistencyOption);

    // normalize what we store in localStorage to MM/YYYY for consistency in the UI
    let displayCompetence = '';
    if (competenceInput) {
      const digits = competenceInput.replace(/[^0-9]/g, '');
      if (digits.length === 6) {
        // if input was YYYYMM (e.g. 202509) or MMYYYY (092025)
        if (
          digits.substring(0, 4).match(/^\d{4}$/) &&
          Number(digits.substring(0, 4)) > 1900
        ) {
          const yyyy = digits.substring(0, 4);
          const mm = digits.substring(4);
          displayCompetence = `${mm}/${yyyy}`;
        } else {
          const mm = digits.substring(0, 2);
          const yyyy = digits.substring(2);
          displayCompetence = `${mm}/${yyyy}`;
        }
      }
    }

    localStorage.setItem('@Diretiva:controle:filter:name', name);
    localStorage.setItem(
      '@Diretiva:controle:filter:competence',
      displayCompetence || ''
    );
    localStorage.setItem(
      '@Diretiva:controle:filter:situation',
      JSON.stringify(selectedOption)
    );
    localStorage.setItem(
      '@Diretiva:controle:filter:monthly',
      JSON.stringify(selectedMonthlyOption)
    );
    localStorage.setItem(
      '@Diretiva:controle:filter:consistency',
      JSON.stringify(selectedConsistencyOption)
    );
  }, [consistencyOptions, monthlyOptions, situationOptions]);

  return (
    <>
      <Container>
        <Header>
          <div>
            <FaAddressCard size={20} color="#44546a" />
            <h1>Controle</h1>
          </div>

          <Link to="/">
            <FaTimes size={20} color="#44546a" />
          </Link>
        </Header>
        <Controls>
          <button type="button" onClick={alterView}>
            <FaEye />
            <span>Visualização</span>
          </button>
          {!showTable && (
            <>
              <PermissionComponent level={5}>
                <Link
                  to={{ pathname: '/controle/edit', state: { id: client.id } }}
                >
                  <FaEdit />
                  <span>Editar</span>
                </Link>
              </PermissionComponent>
              <PermissionComponent level={9}>
                <button type="button" onClick={() => confirmDelete()}>
                  <FaTrash />
                  <span>Excluir</span>
                </button>
              </PermissionComponent>
            </>
          )}
          {showTable && !matchParamsId && (
            <>
              <button
                className="id"
                type="button"
                onClick={confirmTransmitPayments}
              >
                <FaCloudArrowUp />
                <span>Transmitir boleto(s)</span>
              </button>
            </>
          )}
          <button
            className="id"
            type="button"
            onClick={handleCheckPaymentsByCompetence}
          >
            <FaSync />
            <span>Atualizar</span>
          </button>
          {/* <PermissionComponent level={6}>
            {client.active && (
              <button type="button" onClick={() => setVisiblePopUp(true)}>
                <FaThumbsDown />
                <span>Inativar PJ</span>
              </button>
            )}
          </PermissionComponent> */}
          {!matchParamsId && clients.length > 1 && (
            <div>
              <button type="button" onClick={handlePrevItem}>
                <FaChevronLeft />
              </button>
              <span>
                {absoluteIndex} de {clients.length}
              </span>
              <button type="button" onClick={handleNextItem}>
                <FaChevronRight />
              </button>
            </div>
          )}
          <div className="billets-counter">
            <span className="counter-label">Boletos com bug:</span>
            <span className="counter-value">{billetsCount}</span>
          </div>
        </Controls>
        {showTable && (
          <Filter ref={filterRef} onSubmit={() => {}}>
            <Input
              name="name"
              className="name"
              label="Nome"
              defaultValue={nameSearch}
            />
            <InputMask
              name="competence"
              className="competence"
              label="Competência"
              mask="99/9999"
              defaultValue={defaultCompetence}
            />
            <Select
              label="Situação"
              name="situation"
              className="situation"
              options={situationOptions}
              defaultValue={selectedSituation}
            />
            <Select
              label="Mensalidade"
              name="monthly"
              className="monthly"
              options={monthlyOptions}
              defaultValue={selectedMonthly}
            />
            <Select
              label="Consistência"
              name="consistency"
              className="consistency"
              options={consistencyOptions}
              defaultValue={selectedConsistency}
            />
            <button
              type="button"
              onClick={searchByName}
              className="search-name"
            >
              <FaSearch />
              <span>N</span>
            </button>
          </Filter>
        )}

        {loading || !company ? (
          <TableLoading />
        ) : (
          <Content className="content">
            {showTable ? (
              <TableContainer>
                <thead>
                  <tr>
                    <th className="has-mp">
                      <input
                        type="checkbox"
                        checked={
                          clients.length > 0 &&
                          selectedIds.length === clients.length
                        }
                        onChange={() => handleIdsChange('all')}
                      />
                    </th>
                    <th className="has-mp" />
                    <th className="has-mp" />
                    <th className="document">CPF/CNPJ</th>
                    <th className="name">Nome</th>
                    <th className="payments">Mensalidades</th>
                    <th className="billets">Boletos</th>
                    <th className="billet-situations">Situações</th>
                  </tr>
                </thead>
                <tbody>
                  {clients &&
                    clients.map((clientItem, index) => {
                      const hasBillets =
                        Array.isArray(clientItem.billets) &&
                        clientItem.billets.length > 0;
                      const billetSituations = hasBillets
                        ? clientItem.billets
                            .map(
                              b =>
                                b?.billet_situations?.description ||
                                b?.situation_text ||
                                b?.situation ||
                                ''
                            )
                            .filter(Boolean)
                        : [];
                      const hasConfirmedEntry = billetSituations.some(
                        situation => {
                          if (!situation) return false;
                          const situationLower = situation.trim().toLowerCase();
                          return (
                            situationLower === 'entrada confirmada' ||
                            situationLower === 'liquidação'
                          );
                        }
                      );
                      const hasMonthly =
                        hasBillets &&
                        clientItem.billets.some(
                          b => b && b.month_payment === true
                        );
                      const situationsText = billetSituations.join(', ');
                      const normalizeValue = value =>
                        Number(Number(value || 0).toFixed(2));
                      const monthlyTotal = normalizeValue(
                        typeof clientItem.monthly_payments_total === 'number'
                          ? clientItem.monthly_payments_total
                          : Array.isArray(clientItem.monthly_payments)
                          ? clientItem.monthly_payments.reduce(
                              (sum, mp) => sum + (mp.price || 0),
                              0
                            )
                          : 0
                      );
                      const billetsTotal = normalizeValue(
                        clientItem.billets_total
                      );
                      const hasDifferentTotals = monthlyTotal !== billetsTotal;

                      return (
                        <tr
                          key={clientItem.id}
                          className={`hover ${
                            hasDifferentTotals ? 'inconsistent-row' : ''
                          }`}
                          onClick={() => getDetails(clientItem.id, index)}
                        >
                          <td className="has-mp">
                            <input
                              name="select"
                              type="checkbox"
                              checked={selectedIds.includes(clientItem.id)}
                              onChange={e => {
                                e.stopPropagation();
                                handleIdsChange(clientItem.id);
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td className="has-mp">
                            {hasConfirmedEntry ? (
                              <FaThumbsUp size={14} color="#006229" />
                            ) : (
                              <FaThumbsDown size={14} color="#E53935" />
                            )}
                          </td>
                          <td className="has-mp">
                            {clientItem.monthly_payments &&
                            clientItem.monthly_payments.length > 0 ? (
                              <FaSmile size={14} color="#006229" />
                            ) : (
                              <FaFrown size={14} color="#E53935" />
                            )}
                          </td>
                          <td className="document">{clientItem?.document}</td>
                          <td className="name">{clientItem?.name}</td>
                          <td className="payments">
                            {clientItem.monthly_payments &&
                            clientItem.monthly_payments.length > 0
                              ? clientItem.monthly_payments.map((mp, i) => (
                                  <div key={i}>
                                    {new Intl.NumberFormat('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                    }).format(mp.price || 0)}
                                  </div>
                                ))
                              : new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(0)}
                          </td>
                          <td className="billets">
                            {new Intl.NumberFormat('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            }).format(clientItem.billets_total || 0)}
                          </td>
                          <td className="billet-situations">
                            {hasBillets ? (
                              <>
                                {situationsText ? (
                                  <span>{situationsText}</span>
                                ) : (
                                  <span>—</span>
                                )}
                                {hasMonthly && (
                                  <span className="mp-badge">Mensalidade</span>
                                )}
                              </>
                            ) : (
                              <span>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </TableContainer>
            ) : (
              <DetailsContainer>
                <BasicInfo>
                  <h4>INFORMAÇÕES BÁSICAS</h4>
                  <section>
                    <div className="document">
                      <label>Documento</label>
                      <input name="document" value={client.document} readOnly />
                    </div>
                    <div className="name">
                      <label>Nome</label>
                      <input name="name" value={client.name} readOnly />
                    </div>
                    <div className="client_type">
                      <label>Tipo de cliente</label>
                      <input
                        name="client_type"
                        value={client.client_type_label}
                        readOnly
                      />
                    </div>
                  </section>
                </BasicInfo>
                {client.addresses && client.addresses.length > 0 && (
                  <Addresses>
                    <h4>ENDEREÇOS</h4>
                    {client.addresses.map(address => (
                      <Address key={address.id}>
                        <section>
                          <div className="street">
                            <label>Endereço</label>
                            <input
                              name="street"
                              value={address.street}
                              readOnly
                            />
                          </div>
                          <div className="number">
                            <label>Nº</label>
                            <input
                              name="number"
                              value={address.number}
                              readOnly
                            />
                          </div>
                        </section>
                      </Address>
                    ))}
                  </Addresses>
                )}
                {client.contacts && client.contacts.length > 0 && (
                  <Contacts>
                    <h4>CONTATOS</h4>
                    {client.contacts.map(contact => (
                      <section key={contact.id}>
                        <div className="contact_type">
                          <label>Tipo</label>
                          <input
                            name="contact_type"
                            value={contact.type_label}
                            readOnly
                          />
                        </div>
                        <div className="content">
                          <label>{contact.type_label}</label>
                          <input
                            name="content"
                            value={contact.content}
                            readOnly
                          />
                        </div>
                      </section>
                    ))}
                  </Contacts>
                )}
                {client.related && client?.related?.length > 0 && (
                  <RelatedList>
                    <h4>PESSOAS RELACIONADAS</h4>
                    {client.related.map(related => (
                      <Related key={related.id}>
                        <section>
                          <div className="related_document">
                            <label>CPF</label>
                            <input
                              name="related_document"
                              value={related.document}
                              readOnly
                            />
                          </div>
                          <div className="related_name">
                            <label>Nome</label>
                            <input
                              name="related_name"
                              value={related.name}
                              readOnly
                            />
                          </div>
                        </section>
                      </Related>
                    ))}
                  </RelatedList>
                )}
                {client.monthly_payments && client.monthly_payments.length > 0 && (
                  <RelatedList className="monthly-payments-list">
                    <h4>MENSALIDADES</h4>
                    {client.monthly_payments.map((mp, idx) => (
                      <Related
                        key={mp.id || idx}
                        className="monthly-payment-item"
                      >
                        <section className="monthly-payment-section">
                          <div className="mp-value">
                            <label>Valor</label>
                            <input
                              name={`mp_value_${idx}`}
                              value={new Intl.NumberFormat('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              }).format(mp.price || 0)}
                              readOnly
                            />
                          </div>
                        </section>
                      </Related>
                    ))}
                  </RelatedList>
                )}
              </DetailsContainer>
            )}
          </Content>
        )}

        <Pagination
          loading={loading ? 1 : 0}
          currentPage={currentPage}
          pages={totalPages}
          totalDocs={totalClients}
          handlePage={handlePage}
        />
      </Container>
      {(deleteLoading || loading) && <Loading />}

      <PopUp
        visible={visiblePopUp}
        setVisible={setVisiblePopUp}
        body={
          <FormContainer onSubmit={handleInativarPJ} ref={formRef}>
            <DatePicker
              name="end_day"
              label="Data de Encerramento"
              selected={endDate}
              onChange={date => setEndDate(date)}
            />
            <br />
            <SelectWithAddNewOptionButton
              name="inactive_type"
              className="inactive"
              label="Tipo de encerramento"
              options={inactiveTypeOptions}
              value={inactiveType}
              onChange={option => setInactiveType(option)}
              placeholder="Selecione um tipo"
            />
            <br />
            <br />
            <Button className="popupButton">Salvar</Button>
          </FormContainer>
        }
        title="Inativar PJ"
        icon={<FaCog size="18" color="#01579b" />}
      />
    </>
  );
};

export default List;
